import { useState, useEffect } from "react";
import {
  GroupTextField,
  GroupTextFieldRow,
  Heading,
  TextField,
  Badge,
  Icon,
} from "@elaniintc/goes-design-system";
import { TemplateSelector } from "./components/template-selector";
import { DocGeneratorCreateForm } from "./forms/create-form";
import { useDocumentGeneratorNode } from "./hooks";
import {
  useGetTemplate,
  useGetTemplateVersions,
} from "@/queries/templates/templates.api";

export const DocumentGeneratorNode = () => {
  const {
    selectedTemplate,
    variableName,
    setVariableName,
    isVariableNameValid,
    variableNameError,
  } = useDocumentGeneratorNode();
  const [isDateSelected, setIsDateSelected] = useState(false);
  const [isIDSelected, setIDSelected] = useState(false);

  const { data: templateData } = useGetTemplate(selectedTemplate?.id);

  const { data: parentTemplates } = useGetTemplateVersions(
    templateData?.data?.parent_template_id || undefined,
  );

  useEffect(() => {
    setIsDateSelected(variableName.include_date ?? false);
    setIDSelected(variableName.include_request_id ?? false);
  }, [variableName.include_date, variableName.include_request_id]);

  const isLastVersion = () => {
    if (!parentTemplates?.data || !selectedTemplate?.version) {
      return true;
    }

    const lastTemplate = parentTemplates.data.find(({ is_latest }) =>
      Boolean(is_latest),
    );

    if (!lastTemplate) return true;

    return selectedTemplate.version >= lastTemplate.version;
  };

  const handleChangeName = (event: { target: { value: string } }) => {
    const name = event.target.value.slice(0, 200);
    setVariableName({
      base: name,
      include_request_id: isIDSelected,
      include_date: isDateSelected,
    });
  };

  const handleClick = (type: "date" | "id") => {
    if (type === "date") {
      setIsDateSelected((prev) => !prev);
      setVariableName({
        ...variableName,
        include_date: !isDateSelected,
      });
    } else {
      setIDSelected((prev) => !prev);
      setVariableName({
        ...variableName,
        include_request_id: !isIDSelected,
      });
    }
  };

  return (
    <div className="py-3 flex flex-col gap-3">
      <GroupTextField
        label="Plantilla de documento"
        required
        className="w-full pt-3 pb-6 px-4 border-b-1 border-neutral-quiet"
        helperText=""
      >
        <GroupTextFieldRow>
          <TemplateSelector
            isLatestVersion={isLastVersion()}
            placeholder="Seleccionar una plantilla"
          />
        </GroupTextFieldRow>
      </GroupTextField>

      {/* Solo mostrar la sección "Datos para sobreescribir" cuando se seleccione una plantilla */}
      {selectedTemplate && <DocGeneratorCreateForm />}

      {/* Solo mostrar la sección "Archivo generado" cuando se seleccione una plantilla */}
      {selectedTemplate && (
        <div className="w-full pt-3 pb-6 px-4 flex flex-col gap-4">
          <Heading variant="h6">Archivo generado</Heading>
          <TextField
            label="Nombre del archivo"
            required
            placeholder="Escribe el nombre de la variable"
            helperText="Elige un nombre para la variable que crearás en este nodo."
            value={variableName.base}
            error={
              !isVariableNameValid && variableName.base
                ? variableNameError || "Nombre inválido"
                : undefined
            }
            maxLength={200}
            onChange={handleChangeName}
          />
          <div className="flex flex-row gap-2">
            <Badge
              variant={isDateSelected ? "action" : "neutral"}
              onClick={() => handleClick("date")}
            >
              Fecha
              <Icon
                type={isDateSelected ? "fas" : "far"}
                name={isDateSelected ? "circle-check" : "circle"}
                className="cursor-pointer rounded-lg"
              />
            </Badge>
            <Badge
              variant={isIDSelected ? "action" : "neutral"}
              onClick={() => handleClick("id")}
            >
              ID de Solicitud
              <Icon
                type={isIDSelected ? "fas" : "far"}
                name={isIDSelected ? "circle-check" : "circle"}
                className="cursor-pointer rounded-lg"
              />
            </Badge>
          </div>
        </div>
      )}
    </div>
  );
};
